import React, { FC, useMemo } from "react";
import { useScannerRegistration, type ScannerHintConfig, type ScannerHandlerPriority } from "@app/scanner";

/**
 * Интерфейс пропсов для компонента BarcodeScanner.
 */
interface BarcodeScannerProps {
    /**
     * Функция обратного вызова, вызываемая при сканировании штрихкода.
     * @param barcode - Отсканированный штрихкод.
     */
    onScan: (barcode: string) => void | Promise<void>;
    /** Включён ли обработчик сканирования. */
    enabled?: boolean;
    /** Приоритет обработчика в app-level scanner registry. */
    priority?: ScannerHandlerPriority;
    /** Подсказка для app-level плашки ожидания сканирования. */
    hint?: ScannerHintConfig;
    /** Разрешает принимать скан, когда фокус находится в текстовом поле. */
    captureInEditable?: boolean;
    /** Источник ошибок для `reportError`. */
    errorSource?: string;
}

/**
 * Компонент для сканирования штрихкодов.
 *
 * @example
 *
 *  const handleScan = (barcode) => {
 *   console.log('Сканированный штрихкод:', barcode);
 *   // Дальнейшая обработка штрихкода
 *  };
 *
 *  <BarcodeScanner onScan={handleScan} />
 */
const BarcodeScanner: FC<BarcodeScannerProps> = ({
    onScan,
    enabled = true,
    priority = "screen",
    hint,
    captureInEditable = true,
    errorSource = "BarcodeScanner.onScan",
}) => {
    const registration = useMemo(
        () => ({
            enabled,
            priority,
            hint,
            captureInEditable,
            onScan,
            errorSource,
        }),
        [captureInEditable, enabled, errorSource, hint, onScan, priority],
    );

    useScannerRegistration(registration);
    return <></>;
};

export default BarcodeScanner;
